import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@munifocus/ui';
import type { InspectionView } from '@munifocus/contracts-gov';
import { useMutationToast } from '../../lib/mutation-toast';
import {
  useInspectionTemplates,
  useInspectors,
  useScheduleInspection,
} from '../../lib/permits-api';

/** `YYYY-MM-DDTHH:mm` for a datetime-local input, in the browser's time. */
export function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** Tomorrow at 9:00, the default slot for a new inspection. */
function nextMorning(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

/**
 * Book an inspection: which checklist, when, who, and where — the address is
 * taken from the permit application or file it was opened from when there is
 * one.
 */
export function ScheduleInspectionDialog({
  lg,
  open,
  onClose,
  initial,
  onScheduled,
}: {
  lg: string;
  open: boolean;
  onClose: () => void;
  initial?: { permitApplicationId?: string; fileId?: string; propertyAddress?: string };
  onScheduled?: (inspection: InspectionView) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const templates = useInspectionTemplates(lg);
  const inspectors = useInspectors(lg, open);
  const schedule = useScheduleInspection(lg);
  const [templateId, setTemplateId] = useState('');
  const [when, setWhen] = useState(toLocalInput(nextMorning()));
  const [inspectorId, setInspectorId] = useState('');
  const [address, setAddress] = useState(initial?.propertyAddress ?? '');
  const [pid, setPid] = useState('');
  const fr = i18n.language.startsWith('fr');
  const active = (templates.data ?? []).filter((x) => x.isActive);
  const linked = Boolean(initial?.permitApplicationId || initial?.fileId);
  const valid = templateId && when && (linked || address.trim().length >= 5);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.inspections.schedule.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {active.length === 0 && !templates.isLoading && (
            <Typography variant="body2" color="text.secondary">
              {t('gov.inspections.schedule.noTemplates')}
            </Typography>
          )}
          <TextField
            select
            label={t('gov.inspections.fields.checklist')}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            required
            fullWidth
          >
            {active.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                {fr ? tpl.nameFr : tpl.nameEn}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="datetime-local"
            label={t('gov.inspections.fields.when')}
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            fullWidth
          />
          <TextField
            select
            label={t('gov.inspections.fields.inspector')}
            value={inspectorId}
            onChange={(e) => setInspectorId(e.target.value)}
            helperText={t('gov.inspections.schedule.inspectorHelp')}
            fullWidth
          >
            <MenuItem value="">{t('gov.inspections.unassigned')}</MenuItem>
            {(inspectors.data ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('gov.inspections.fields.property')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            helperText={linked ? t('gov.inspections.schedule.addressFromLink') : undefined}
            required={!linked}
            fullWidth
          />
          <TextField
            label={t('gov.inspections.fields.pid')}
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || schedule.isPending}
          onClick={async () => {
            try {
              const created = await schedule.mutateAsync({
                templateId,
                scheduledFor: new Date(when).toISOString(),
                inspectorId: inspectorId || undefined,
                propertyAddress: address.trim() || undefined,
                pid: pid.trim() || undefined,
                permitApplicationId: initial?.permitApplicationId,
                fileId: initial?.fileId,
              });
              toast.success('gov.inspections.schedule.done');
              onClose();
              onScheduled?.(created);
            } catch (err) {
              toast.error(err, 'gov.admin.errors.actionFailed');
            }
          }}
        >
          {t('gov.inspections.schedule.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
